import { useImages } from "@features/images/use-images";
import { useUser } from "@features/user/use-user";
import { Button, TextArea } from "@radix-ui/themes";
import { ExternalLink, MessageCircleHeart, Star } from "lucide-react";
import posthog from "posthog-js";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "react-query";
import { ResponsiveModal } from "./responsive-modal";

const FEEDBACK_DISMISSED_KEY = "feedback_modal_dismissed_at";
const FEEDBACK_CLOSED_PERMANENTLY_KEY = "feedback_modal_closed_permanently";

let pleaseShowItNow = -1;

export const useShowFeedbackModalNow = () => {
  const queryClient = useQueryClient();
  return (delay = 0) => {
    pleaseShowItNow = Date.now() + delay;
    setTimeout(() => {
      // Force modal to re-check conditions after the delay
      queryClient.invalidateQueries({ queryKey: ["feedbackModal"] });
    }, delay + 100);
  };
};

export const useShouldShowFeedbackModal = () => {
  const { user, credits } = useUser();
  const { images } = useImages();

  const { data: showItNow } = useQuery({
    queryKey: ["feedbackModal"],
    queryFn: () => {
      return pleaseShowItNow > 0 && pleaseShowItNow < Date.now();
    },
  });

  // Check if feedback was already given
  const hasGivenFeedback =
    user.data?.free_credits_actions?.includes("feedback");

  // Check if permanently closed
  const isClosedPermanently =
    localStorage.getItem(FEEDBACK_CLOSED_PERMANENTLY_KEY) === "true";

  // Check if dismissed and if 48h have passed
  const dismissedAt = localStorage.getItem(FEEDBACK_DISMISSED_KEY);
  const now = Date.now();
  const fortyEightHours = 48 * 60 * 60 * 1000;
  const wasDismissedRecently =
    dismissedAt && now - parseInt(dismissedAt, 10) < fortyEightHours;

  // Get most recent image date
  const mostRecentImage = images.data?.[0];
  const mostRecentImageDate = mostRecentImage?.created_at || 0;
  const oneDayAgo = now - 24 * 60 * 60 * 1000;
  const isOldestImage24hAgo =
    mostRecentImageDate < oneDayAgo && mostRecentImageDate > 0;

  // Check conditions
  const isNotFreePlan = user.data?.plan && user.data.plan !== "free";
  const hasLowCredits = credits < 6;

  // Should show if:
  // - Never gave feedback AND not permanently closed AND (not dismissed OR dismissed more than 48h ago)
  // - AND ((not free plan AND oldest image > 24h) OR credits < 6)
  const shouldShow =
    !hasGivenFeedback &&
    !isClosedPermanently &&
    !wasDismissedRecently &&
    user.data &&
    images.data &&
    images.data.length > 0 &&
    ((isNotFreePlan && isOldestImage24hAgo) || hasLowCredits || showItNow);

  return shouldShow;
};

export const FeedbackModal = () => {
  const { t } = useTranslation();
  const { claimFreeCredits } = useUser();
  const shouldShow = useShouldShowFeedbackModal();

  const [isOpen, setIsOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState("");
  const [step, setStep] = useState<
    "rating" | "feedback" | "trustpilot" | "thankyou"
  >("rating");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (shouldShow && !isOpen) {
      // Small delay before showing
      const timer = setTimeout(() => {
        setIsOpen(true);
        posthog.capture("feedback_modal_shown");
      }, 2000);
      pleaseShowItNow = -1;
      return () => clearTimeout(timer);
    }
  }, [shouldShow, isOpen]);

  const handleClose = () => {
    localStorage.setItem(FEEDBACK_CLOSED_PERMANENTLY_KEY, "true");
    setIsOpen(false);
  };

  const handleRatingClick = async (selectedRating: number) => {
    setRating(selectedRating);
    posthog.capture("feedback_rating_selected", { rating: selectedRating });

    if (selectedRating >= 4) {
      setStep("trustpilot");
    } else {
      setStep("feedback");
    }
  };

  const handleSubmitFeedback = async () => {
    setIsSubmitting(true);

    // Track the feedback
    posthog.capture("feedback_submitted", {
      rating,
      feedback: feedbackText,
      type: "text_feedback",
    });

    // Claim the free credits and store feedback data
    await claimFreeCredits("feedback", {
      rating,
      text: feedbackText,
      type: "text_feedback",
    });

    setStep("thankyou");
    setIsSubmitting(false);
  };

  const handleTrustpilotClick = async () => {
    setIsSubmitting(true);

    // Track the trustpilot click
    posthog.capture("feedback_trustpilot_redirect", { rating });

    // Open Trustpilot review page immediately
    window.open("https://www.trustpilot.com/evaluate/photographe.ai", "_blank");

    // Wait 1 second then give credits automatically
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Track completion
    posthog.capture("feedback_submitted", {
      rating,
      type: "trustpilot",
    });

    // Claim the free credits and store rating
    await claimFreeCredits("feedback", {
      rating,
      type: "trustpilot",
    });

    setStep("thankyou");
    setIsSubmitting(false);
  };

  const renderStars = () => {
    return (
      <div className="flex justify-center gap-2 my-8">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => handleRatingClick(star)}
            onMouseEnter={() => setHoveredRating(star)}
            onMouseLeave={() => setHoveredRating(0)}
            className="transition-transform hover:scale-110 focus:outline-none"
          >
            <Star
              className={`w-12 h-12 transition-colors ${
                star <= (hoveredRating || rating)
                  ? "fill-yellow-400 text-yellow-400"
                  : "text-slate-300 dark:text-slate-600"
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  const renderRatingStep = () => (
    <div className="text-center py-4">
      <div className="flex justify-center mb-4">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
          <MessageCircleHeart className="w-8 h-8 text-white" />
        </div>
      </div>
      <h3 className="text-xl font-semibold mb-2 text-slate-900 dark:text-white">
        {t("feedback_modal.title")}
      </h3>
      <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
        {t("feedback_modal.subtitle")}
      </p>
      <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
        {t("feedback_modal.reward")}
      </p>
      {renderStars()}
      <p className="text-xs text-slate-500 dark:text-slate-500">
        {t("feedback_modal.click_star")}
      </p>
    </div>
  );

  const renderFeedbackStep = () => (
    <div className="py-4">
      <h3 className="text-lg font-semibold mb-2 text-slate-900 dark:text-white text-center">
        {t("feedback_modal.feedback_title")}
      </h3>
      <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 text-center">
        {t("feedback_modal.feedback_subtitle")}
      </p>
      <TextArea
        value={feedbackText}
        onChange={(e) => setFeedbackText(e.target.value.slice(0, 2048))}
        placeholder={t("feedback_modal.feedback_placeholder")}
        className="w-full min-h-[120px] mb-2"
      />
      <p className="text-xs text-slate-400 mb-4 text-right">
        {feedbackText.length}/2048
      </p>
      <Button
        size="3"
        className="w-full"
        onClick={handleSubmitFeedback}
        disabled={isSubmitting || feedbackText.trim().length === 0}
      >
        {isSubmitting
          ? t("feedback_modal.submitting")
          : t("feedback_modal.submit")}
      </Button>
    </div>
  );

  const renderTrustpilotStep = () => (
    <div className="text-center py-4">
      <div className="flex justify-center mb-4">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
          <Star className="w-8 h-8 text-white fill-white" />
        </div>
      </div>
      <h3 className="text-xl font-semibold mb-2 text-slate-900 dark:text-white">
        {t("feedback_modal.trustpilot_title")}
      </h3>
      <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
        {t("feedback_modal.trustpilot_subtitle")}
      </p>
      <Button
        size="3"
        className="w-full !bg-[#00b67a] hover:!bg-[#00a06a]"
        onClick={handleTrustpilotClick}
      >
        <ExternalLink className="w-4 h-4 mr-2" />
        {t("feedback_modal.trustpilot_button")}
      </Button>
    </div>
  );

  const renderThankYouStep = () => (
    <div className="text-center py-8">
      <div className="flex justify-center mb-4">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center">
          <span className="text-4xl">🎉</span>
        </div>
      </div>
      <h3 className="text-2xl font-bold mb-2 text-slate-900 dark:text-white">
        {t("feedback_modal.thankyou_title")}
      </h3>
      <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
        {t("feedback_modal.thankyou_credits")}
      </p>
      <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
        {t("feedback_modal.thankyou_help")}
      </p>
      <Button size="3" variant="soft" className="w-full" onClick={handleClose}>
        {t("feedback_modal.close")}
      </Button>
    </div>
  );

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={handleClose}
      title=""
      height="auto"
      maxHeight="80vh"
      className="!max-w-md"
    >
      <div className="px-2">
        {step === "rating" && renderRatingStep()}
        {step === "feedback" && renderFeedbackStep()}
        {step === "trustpilot" && renderTrustpilotStep()}
        {step === "thankyou" && renderThankYouStep()}
      </div>
    </ResponsiveModal>
  );
};
